import EditIcon from "@mui/icons-material/Edit";
import {
  Avatar,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { t } from "i18next";
import moment from "moment";
import React, { useRef, useState } from "react";
import { useSelector } from "react-redux";
import appcolors from "../../constants/Colors";
import { CURRENCY, DEFAULT_VAT_PERCENTAGE } from "../../constants/Variables";
import {
  calculateVat,
  renderFinalProductPrice,
  roundTo2Decimal,
} from "../../utils";
import GlobalButton from "../CustomButton";

const tableStyle = {
  "&.MuiTableContainer-root .MuiTableHead-root": {
    backgroundColor: appcolors.primary,
  },
  "&.MuiTableContainer-root .MuiTableHead-root .MuiTableRow-root .MuiTableCell-root":
    {
      padding: "6px 16px",
      color: appcolors.white,
      fontSize: "1rem",
    },
  "& .view_btn": {
    border: "1px solid grey",
    height: 35,
    width: 75,
    fontSize: "0.875rem",
    borderRadius: "6px",
  },
};

export default function ProductTable({
  minWidth,
  tableHeader,
  tableData,
  displayRows,
  showEdit,
  handleEdit,
  handleChangeProductStatus,
  onOpenVariantsDialog,
  showActions = true,
  paginatedData,
  onPageChange,
  onRowsPerPageChange,
  isLoading,
}) {
  // Get Current Language from Redux
  const { currentLanguage } = useSelector((state) => state.translationReducer);
  // Get Settings from Redux
  const settings = useSelector((state) => state.settingsReducer.settings);

  return (
    <React.Fragment key="product-table">
      <TableContainer sx={tableStyle}>
        <Table
          sx={{
            minWidth: minWidth ? minWidth : 900,
          }}
        >
          <TableHead>
            <TableRow>
              {tableHeader?.map((header) => (
                <TableCell key={header}>{t(header)}</TableCell>
              ))}
              {showActions && (
                <TableCell
                  sx={{
                    position: "sticky",
                    right: 0,
                    zIndex: 3,
                    px: 0,
                    backgroundColor: "primary.main",
                  }}
                >
                  {t("common.actions")}
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableData?.map((row, index) => {
              // This is calculated fot both the product and the base packages
              const finalProductPrice = renderFinalProductPrice(
                row.price,
                row.discount,
                row.profitMargin,
              );

              // This is calculated for both the product and the base packages
              const productVat = calculateVat(
                finalProductPrice,
                settings?.tax || DEFAULT_VAT_PERCENTAGE,
              );

              // This is calculated for both the product and the base packages
              const finalPriceAfterVat = roundTo2Decimal(
                finalProductPrice + productVat,
              );

              return (
                <TableRow key={index}>
                  {displayRows.map((val) => (
                    <React.Fragment key={val}>
                      {val === "created_date" ? (
                        <TableCell>
                          {moment(row.createdAt).format("DD/MM/YYYY")}
                          <br />
                          {moment(row.createdAt).format("hh:mm:ss a")}
                        </TableCell>
                      ) : val === "product_sku" ? (
                        <TableCell>
                          {!row?.isVariant ? (
                            row.sku
                          ) : (
                            <GlobalButton
                              variant={"outlined"}
                              btnText={"view"}
                              className={"view_btn"}
                              styleProps={{
                                height: 40,
                                width: "auto",
                              }}
                              handleClickBtn={() => onOpenVariantsDialog?.(row)}
                            />
                          )}
                        </TableCell>
                      ) : val === "category_name" ? (
                        <TableCell sx={{ textTransform: "capitalize" }}>
                          {row?.category?.name?.[currentLanguage]}
                        </TableCell>
                      ) : val === "sub_category_name" ? (
                        <TableCell sx={{ textTransform: "capitalize" }}>
                          {row?.subCategory?.length > 0 ? (
                            row?.subCategory.map((x) => (
                              <Typography variant="body2" mb={0.5}>
                                {x?.name?.[currentLanguage]}
                              </Typography>
                            ))
                          ) : (
                            <Typography variant="body2" color="info.main">
                              {t("notAddedYet")}
                            </Typography>
                          )}
                        </TableCell>
                      ) : val === "sub_sub_category_name" ? (
                        <TableCell sx={{ textTransform: "capitalize" }}>
                          {row?.subSubCategory?.length > 0 ? (
                            row?.subSubCategory.map((x) => (
                              <Typography variant="body2" mb={0.5}>
                                {x?.name?.[currentLanguage]}
                              </Typography>
                            ))
                          ) : (
                            <Typography variant="body2" color="info.main">
                              {t("notAddedYet")}
                            </Typography>
                          )}
                        </TableCell>
                      ) : val === "product_price" ? (
                        <TableCell sx={{ textTransform: "capitalize" }}>
                          {!row?.isVariant ? `${row.price} ${CURRENCY}` : "_"}
                        </TableCell>
                      ) : val === "product_profit_margin" ? (
                        <TableCell sx={{ textTransform: "capitalize" }}>
                          {!row?.isVariant ? `${row.profitMargin || 0}%` : "_"}
                        </TableCell>
                      ) : val === "product_discount" ? (
                        <TableCell sx={{ textTransform: "capitalize" }}>
                          {!row?.isVariant
                            ? `${row.discount} ${CURRENCY}`
                            : "_"}
                        </TableCell>
                      ) : val === "product_final_price" ? (
                        <TableCell sx={{ textTransform: "capitalize" }}>
                          {!row?.isVariant
                            ? `${row.finalPrice} ${CURRENCY}`
                            : "_"}
                        </TableCell>
                      ) : val === "name_image" ? (
                        <TableCell>
                          <Stack
                            direction={"row"}
                            alignItems={"center"}
                            gap={1}
                            width={"400px"}
                          >
                            <Avatar
                              src={row.coverImage}
                              sx={{
                                height: 55,
                                width: 55,
                                borderRadius: "6px",
                                backgroundColor: "#edebeb",
                                border: "1px solid #edebeb",
                              }}
                            />
                            <Tooltip title={row.name[currentLanguage]}>
                              <Typography
                                variant={"body1"}
                                textTransform={"capitalize"}
                                sx={{
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  display: "-webkit-box",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: "400px",
                                }}
                              >
                                {row.name[currentLanguage]}
                              </Typography>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      ) : val === "product_initial_quantity" ? (
                        <TableCell>{row.initialQuantity || 0}</TableCell>
                      ) : val === "product_quantity" ? (
                        <TableCell>{row.quantity || 0}</TableCell>
                      ) : val === "product_rating" ? (
                        <TableCell>{row.rating.toFixed(0)}</TableCell>
                      ) : val === "product_variants" ? (
                        <TableCell>
                          {row?.isVariant ? (
                            <GlobalButton
                              variant={"outlined"}
                              btnText={"view"}
                              className={"view_btn"}
                              styleProps={{
                                height: 40,
                              }}
                              handleClickBtn={() => onOpenVariantsDialog?.(row)}
                            />
                          ) : (
                            "_"
                          )}
                        </TableCell>
                      ) : val === "product_status" ? (
                        <TableCell>
                          <Switch
                            checked={row.status}
                            onChange={(e) =>
                              handleChangeProductStatus(e.target.checked, row)
                            }
                            inputProps={{ "aria-label": "controlled" }}
                          />
                        </TableCell>
                      ) : val === "app_vat" ? (
                        <TableCell>
                          {!row?.isVariant
                            ? `${settings?.tax || DEFAULT_VAT_PERCENTAGE}%`
                            : "_"}
                        </TableCell>
                      ) : val === "product_vat_amount" ? (
                        <TableCell>
                          {!row?.isVariant ? `${productVat} ${CURRENCY}` : "_"}
                        </TableCell>
                      ) : val === "product_final_price_after_vat" ? (
                        <TableCell>
                          {!row?.isVariant
                            ? `${finalPriceAfterVat} ${CURRENCY}`
                            : "_"}
                        </TableCell>
                      ) : val === "actions" ? (
                        <TableCell
                          sx={{
                            position: "sticky",
                            right: 0,
                            backgroundColor: "secondary.light",
                            zIndex: 2,
                            minWidth: 50,
                            boxShadow: "inset 4px 0 8px rgba(0,0,0,0.08)",
                          }}
                        >
                          <Stack
                            direction={"row"}
                            alignItems={"center"}
                            gap={1}
                          >
                            {showEdit && (
                              <IconButton onClick={() => handleEdit(row)}>
                                <EditIcon
                                  sx={{ fontSize: 18, color: "primary.main" }}
                                />
                              </IconButton>
                            )}
                          </Stack>
                        </TableCell>
                      ) : (
                        <TableCell>
                          <Typography lineHeight="normal" fontSize={"0.875rem"}>
                            {row[val]}
                          </Typography>
                        </TableCell>
                      )}
                    </React.Fragment>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 20, 50, 100]}
        component="div"
        count={paginatedData.total}
        rowsPerPage={paginatedData.limit}
        page={paginatedData.page - 1}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        labelRowsPerPage={t("rowsPerPage")}
        disabled={isLoading}
      />
    </React.Fragment>
  );
}
